import { useAuth, useOrganization, useOrganizationList, useUser } from '@clerk/clerk-react';
import type { OrganizationMembershipResource } from '@clerk/types';
import { Button, Icon, TFLoadingOverlay } from '@tf/ui';
import type React from 'react';
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { TFErrorTemplate } from '../components/TFErrorBoundary';

interface Props {
	signInPagePath: string;
	checkOrgMembershipFn: (membership: OrganizationMembershipResource) => boolean;
	trackOrgMismatchFn?: (membership: OrganizationMembershipResource) => void;
}

export const AuthenticatedRoutesGuard: React.FC<React.PropsWithChildren<Props>> = ({
	signInPagePath,
	checkOrgMembershipFn,
	trackOrgMismatchFn,
	children,
}) => {
	const { orgId: activeOrgId, isSignedIn, signOut } = useAuth();
	const { membership } = useOrganization();
	const { user } = useUser();

	const {
		isLoaded: isClerkLoaded,
		userMemberships,
		setActive,
	} = useOrganizationList({
		userMemberships: { pageSize: 100 },
	});

	const [isSessionSwitchAttempted, setIsSessionSwitchAttempted] = useState(false);

	// reset after logout
	useEffect(() => {
		if (isSignedIn === false && isSessionSwitchAttempted) {
			// eslint-disable-next-line
			setIsSessionSwitchAttempted(false);
		}
	}, [isSignedIn, isSessionSwitchAttempted]);

	useEffect(() => {
		if (!isClerkLoaded || !isSignedIn || userMemberships.isLoading) {
			return;
		}

		const requiredOrgId = userMemberships.data.find(checkOrgMembershipFn)?.organization.id;

		// Switch org if a user has it in memberships
		if (requiredOrgId && requiredOrgId !== activeOrgId) {
			setActive({ organization: requiredOrgId }).finally(() => {
				setIsSessionSwitchAttempted(true);
			});
		} else {
			// eslint-disable-next-line
			setIsSessionSwitchAttempted(true);
		}
	}, [isClerkLoaded, isSignedIn, userMemberships.isLoading, activeOrgId]);

	if (isSignedIn === false) {
		return <Navigate to={signInPagePath} />;
	}

	if (!isSessionSwitchAttempted || isSignedIn === undefined) {
		return <TFLoadingOverlay />;
	}

	// We checked that user authenticated and switched sessions - now active session must be switched to required org
	// If it's still not, block access
	const isRequiredSessionActive = Boolean(membership && checkOrgMembershipFn(membership));

	if (!isRequiredSessionActive) {
		const userEmail = user?.primaryEmailAddress?.emailAddress ?? '';

		return (
			<TFErrorTemplate
				trackErrorFn={trackOrgMismatchFn && membership ? () => trackOrgMismatchFn(membership) : undefined}
				errorMessage={{
					icon: <Icon.IconFingerprintOff />,
					title: 'You don’t have access to this page',
					// TODO: this text should be customizable for CA?
					description: 'Please contact your workspace admin or contact support if you think this is a mistake',
					primaryAction: (
						<Button
							leftSection={<Icon.IconLogout size={24} />}
							variant="filled"
							size="md"
							h={48}
							fz={16}
							fw={500}
							onClick={() => signOut()}
						>
							Log Out
						</Button>
					),
					secondaryAction: (
						<Button
							leftSection={<Icon.IconMail size={24} />}
							component="a"
							variant="light"
							size="md"
							href="mailto:support@trustform.io"
							h={48}
							fz={16}
							fw={500}
						>
							Contact support
						</Button>
					),
					technicalNote: `${userEmail} | ${window.location.host}`,
				}}
			/>
		);
	}

	return <>{children}</>;
};
